import { useCallback, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import axios from "axios";
import { ButtonProps, useRedirect } from "react-admin";

import { getRawUrlBase, PlanningResourcesEnum } from "shared/enums/resourceMappings";

import { InitialData } from "../types";
import { buildPlanningPayload } from "../utils/planningPayload";
import { computeDefaultValues } from "../utils/computePlanningDefaults";

interface UsePlanningSubmissionParams {
  initialData: InitialData;
  onSuccess?: () => void;
}

interface UsePlanningSubmissionResult {
  defaultValues: Record<string, unknown>;
  isLoading: boolean;
  handleSubmit: (values: FieldValues) => Promise<void>;
  submitButtonProps: Pick<ButtonProps, "disabled">;
}

export const usePlanningSubmission = ({
  initialData,
  onSuccess,
}: UsePlanningSubmissionParams): UsePlanningSubmissionResult => {
  const [isLoading, setIsLoading] = useState(false);
  const redirect = useRedirect();

  const defaultValues = useMemo(() => computeDefaultValues(initialData), [initialData]);

  const postPlannings = useCallback(async (payload: Record<number, unknown>) => {
    return axios({
      method: "post",
      url: `${getRawUrlBase(PlanningResourcesEnum.Plannings)}`,
      data: payload,
    });
  }, []);

  const handleSubmit = useCallback(
    async (values: FieldValues) => {
      setIsLoading(true);
      const preparedData = buildPlanningPayload(values, initialData);

      try {
        const { status } = await postPlannings(preparedData);
        if (status >= 200 && status < 300) {
          redirect(`/${PlanningResourcesEnum.Plannings}`);
          onSuccess?.();
        }
      } catch (error) {
        console.error("Failed to submit FCR RTE plannings:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [initialData, postPlannings, redirect, onSuccess],
  );

  return {
    defaultValues,
    isLoading,
    handleSubmit,
    submitButtonProps: {
      disabled: isLoading,
    },
  };
};
